import { useCallback, useEffect, useRef, useState } from 'react';

import useLoadScript from './useLoadScript';

const DEFAULT_SCOPES = ['https://www.googleapis.com/auth/drive.file'];

const useDrivePicker = ({
  customViews,
  mimeTypes,
  multiselect,
  onError,
  onSuccess,
  scopes,
  setOrigin,
  setIncludeFolders,
  setSelectFolderEnabled,
  showUploadFolders,
  appId = null,
  clientId = null,
  developerKey = null,
  disableDefaultView = false,
  locale = 'en',
  pickerCallback = () => null,
  setParentFolder = '',
  showUploadView = false,
  supportDrives = false,
  token = null,
  viewId = 'DOCS',
}) => {
  const picker = useRef(null);
  const accessToken = useRef(token);
  const { loaded: loadedGapi, error: errorGapi } = useLoadScript({
    url: 'https://apis.google.com/js/api.js',
    options: {
      defer: true,
    },
  });
  const { loaded: loadedGsi, error: errorGsi } = useLoadScript({
    url: 'https://accounts.google.com/gsi/client',
    options: {
      defer: true,
    },
  });
  const [isPickerApiLoaded, setIsPickerApiLoaded] = useState(false);
  const hasLoadedScripts = loadedGapi && !errorGapi && loadedGsi && !errorGsi;

  const setCallback = useCallback(
    (payload) => {
      pickerCallback(payload, accessToken.current);
    },
    [accessToken, pickerCallback],
  );

  const createPicker = useCallback(() => {
    const view = new window.google.picker.DocsView(window.google.picker.ViewId[viewId]);
    if (mimeTypes) view.setMimeTypes(mimeTypes.join(','));
    if (setIncludeFolders) view.setIncludeFolders(true);
    if (setSelectFolderEnabled) view.setSelectFolderEnabled(true);
    if (setParentFolder) view.setParent(setParentFolder);

    const uploadView = new window.google.picker.DocsUploadView();
    if (mimeTypes) uploadView.setMimeTypes(mimeTypes.join(','));
    if (showUploadFolders) uploadView.setIncludeFolders(true);
    if (setParentFolder) uploadView.setParent(setParentFolder);

    const pickerBuilder = new window.google.picker.PickerBuilder()
      .addView(new window.google.picker.DocsView(window.google.picker.ViewId.DOCUMENTS).setMode(window.google.picker.DocsViewMode.LIST))
      .setAppId(appId)
      .setOAuthToken(accessToken.current)
      .setDeveloperKey(developerKey)
      .setLocale(locale)
      .setCallback(setCallback);

    if (setOrigin) {
      pickerBuilder.setOrigin(setOrigin);
    }

    if (!disableDefaultView) {
      pickerBuilder.addView(view);
    }

    if (customViews) {
      customViews.map((customView) => pickerBuilder.addView(customView));
    }

    if (multiselect) {
      pickerBuilder.enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED);
    }

    if (showUploadView) {
      pickerBuilder.addView(uploadView);
    }

    if (supportDrives) {
      pickerBuilder.enableFeature(window.google.picker.Feature.SUPPORT_DRIVES);
    }

    picker.current = pickerBuilder.build();
    picker.current.setVisible(true);

    return true;
  }, [
    accessToken,
    appId,
    customViews,
    developerKey,
    disableDefaultView,
    locale,
    mimeTypes,
    multiselect,
    setCallback,
    setIncludeFolders,
    setOrigin,
    setParentFolder,
    setSelectFolderEnabled,
    showUploadFolders,
    showUploadView,
    supportDrives,
    viewId,
  ]);

  useEffect(() => {
    if (hasLoadedScripts && !isPickerApiLoaded) {
      window.gapi.load('auth');
      window.gapi.load('picker', {
        callback: () => {
          setIsPickerApiLoaded(true);
        },
      });
    }
  }, [hasLoadedScripts, isPickerApiLoaded]);

  const openPicker = () => {
    if (!accessToken.current && isPickerApiLoaded) {
      const newTokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: (scopes ? [...DEFAULT_SCOPES, ...scopes] : DEFAULT_SCOPES).join(' '),
        callback: (response) => {
          accessToken.current = response.access_token;
          onSuccess();
          createPicker();
        },
        error_callback: onError,
      });

      newTokenClient.requestAccessToken();
    }

    if (accessToken.current && hasLoadedScripts && isPickerApiLoaded) {
      onSuccess();
      picker.current.setVisible(true);
    }
  };

  return openPicker;
};

export default useDrivePicker;
